import { useMemo, useEffect } from 'react';
import { useUserInfo } from '@/features/auth/hooks/useUserInfo';
import { useUserPermissions } from '@/shared/hooks/useUserPermissions';
import { useWorkspaceStore } from '@/shared/store/workspace';
import type {
  BusinessAccountInfo,
  PlatformInfo,
} from '@/features/auth/types/auth.types';

export interface UseWorkspaceReturn {
  /** All business accounts linked to the user (`businessUserAccounts`) */
  clients: BusinessAccountInfo[];
  /** Platforms of the currently active business account */
  entities: PlatformInfo[];
  /** ID of the currently active business account (`businessAccount.id`) */
  selectedClientId: string | null;
  /** ID of the auto-selected entity (first platform) */
  selectedEntityId: string | null;
  /** True when the user has more than one business account (`businessUserAccounts.length > 1`) */
  canSwitchClient: boolean;
  canSwitchEntity: boolean;
  isInternal: boolean;
  isLoading: boolean;
  userName: string;
  userEmail: string;
  userProfile: string;
}

/**
 * Composite hook that manages client/entity workspace state.
 *
 * ## Data source mapping
 * - `clients` → `userInfo.businessUserAccounts` (all linked companies)
 * - `selectedClientId` → `userInfo.businessAccount.id` (currently logged-in company)
 * - `entities` → platforms from the selected client in `businessUserAccounts`
 * - `selectedEntityId` → auto-selected first available platform (persisted in store)
 *
 * ## Auto-selection
 * - On load: selects `businessAccount.id` as the active client
 * - When client is set: auto-selects the first entity (platform) from that client
 * - Returning user: restores persisted entity from localStorage
 */
export function useWorkspace(): UseWorkspaceReturn {
  const { data: userInfo, isLoading: isUserLoading } = useUserInfo();
  const permissions = useUserPermissions();
  const { selectedClientId, selectedEntityId, setClient, setEntity } =
    useWorkspaceStore();

  // All companies the user has access to
  const clients: BusinessAccountInfo[] = useMemo(
    () => userInfo?.businessUserAccounts ?? [],
    [userInfo?.businessUserAccounts]
  );

  // Platforms from the currently selected client
  const entities: PlatformInfo[] = useMemo(() => {
    if (!selectedClientId) return [];
    const client = clients.find((c) => c.id === selectedClientId);
    return client?.platforms ?? [];
  }, [selectedClientId, clients]);

  // Auto-select: use the businessAccount.id from user-info as the active client
  useEffect(() => {
    if (isUserLoading || !userInfo?.businessAccount?.id) return;

    const currentBusinessId = userInfo.businessAccount.id;

    // Always sync with backend — businessAccount.id is the source of truth
    if (selectedClientId !== currentBusinessId) {
      setClient(currentBusinessId);
    }
  }, [
    isUserLoading,
    userInfo?.businessAccount?.id,
    selectedClientId,
    setClient,
  ]);

  // Auto-select first entity when client is set and current entity is invalid
  useEffect(() => {
    if (!selectedClientId || entities.length === 0) return;

    if (!selectedEntityId || !entities.some((e) => e.id === selectedEntityId)) {
      setEntity(entities[0].id);
    }
  }, [selectedClientId, entities, selectedEntityId, setEntity]);

  return {
    clients,
    entities,
    selectedClientId,
    selectedEntityId,
    canSwitchClient: clients.length > 1,
    canSwitchEntity: permissions.canSwitchEntity,
    isInternal: permissions.isInternal,
    isLoading: isUserLoading || permissions.isLoading,
    userName: userInfo?.name ?? '',
    userEmail:
      typeof userInfo?.email === 'string'
        ? userInfo.email
        : (userInfo?.email?.email ?? ''),
    userProfile: userInfo?.profile?.description ?? '',
  };
}
